import Container from "@/components/Container";
import Eyebrow from "@/components/Eyebrow";
import Reveal from "@/components/Reveal";
import Section from "@/components/Section";

const COMPARISON = [
  {
    workstream: "Starting point",
    traditional: "A tool someone saw",
    laa: "Your operations, mapped",
  },
  { workstream: "What you get", traditional: "A pilot and a deck", laa: "Systems in production" },
  { workstream: "Measure of success", traditional: "It launched", laa: "It's still used" },
  {
    workstream: "When you find out it won't work",
    traditional: "Month six",
    laa: "In the assessment",
  },
] as const;

const PILLARS = [
  {
    title: "We look at the work, not the tools.",
    body: "The assessment starts with how your business actually runs \u2014 where time goes, where errors happen, where margin leaks. Tool selection comes last, not first.",
  },
  {
    title: "Every opportunity is ranked.",
    body: "Impact, effort, and time to payback for each one, so you can sequence them instead of guessing which to try.",
  },
  {
    title: "Adoption is part of the job.",
    body: "We stay through the part where your team changes how they work. That\u2019s where most of the value is won or lost.",
  },
] as const;

export function AiAdvantage() {
  return (
    <Section tone="raised">
      <Container>
        <Reveal className="mx-auto max-w-[60ch] text-center">
          <Eyebrow variant="teal">How we work</Eyebrow>
          <h2 className="mt-3 font-display text-h2 text-ink-900" style={{ textWrap: "balance" }}>
            Most AI projects die between the demo and the workflow.
          </h2>
          <p className="mt-4 text-body-lg text-ink-600">
            We&rsquo;re built around the gap where that happens.
          </p>
        </Reveal>

        <Reveal className="mt-12">
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-paper-0 p-5 sm:p-8">
            <div className="hidden grid-cols-[1fr_0.75fr_1.25fr] items-center gap-x-8 border-b border-ink-200 pb-4 font-mono text-eyebrow uppercase sm:grid">
              <span className="text-ink-500" />
              <span className="text-ink-500">Most AI projects</span>
              <span className="text-brand-700">With LAA</span>
            </div>

            {COMPARISON.map((row, i) => (
              <div
                key={row.workstream}
                className={`grid grid-cols-1 gap-2 py-5 sm:grid-cols-[1fr_0.75fr_1.25fr] sm:items-center sm:gap-x-8 ${
                  i < COMPARISON.length - 1 ? "border-b border-ink-200" : ""
                }`}
              >
                <span className="font-mono text-eyebrow uppercase text-ink-500 sm:font-sans sm:text-body sm:normal-case sm:text-ink-900">
                  {row.workstream}
                </span>
                <span className="text-body-sm text-ink-500 before:mr-2 before:font-mono before:text-eyebrow before:uppercase before:content-['Most_AI_projects:'] sm:before:hidden">
                  {row.traditional}
                </span>
                <span className="tabular text-body-lg font-semibold text-brand-700 before:mr-2 before:font-mono before:text-eyebrow before:font-normal before:uppercase before:text-ink-500 before:content-['With_LAA:'] sm:before:hidden">
                  {row.laa}
                </span>
              </div>
            ))}
          </div>

          <p className="mt-4 text-body-sm text-ink-500">
            Scope, timeline, and price are agreed before any work starts.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3 md:gap-10">
          {PILLARS.map((pillar, i) => (
            <Reveal key={pillar.title} delay={i * 60}>
              <h3 className="font-display text-h3 text-ink-900">{pillar.title}</h3>
              <p className="mt-3 text-body text-ink-600">{pillar.body}</p>
            </Reveal>
          ))}
        </div>
      </Container>
    </Section>
  );
}

export default AiAdvantage;
